import React, { Component } from "react";
import { Spin } from "antd";
import PaymentsView from "./PaymentsView";
import AddPaymentModal from "./AddPaymentModal";
import {
  getTelemedicinePayments,
  createManualPayment
} from "../../../services";
import {
  showSuccessNotification,
  showErrorNotification,
  getProp
} from "../../../helpers";

export default class PaymentsContainer extends Component {
  constructor(props) {
    super(props);

    // Parse initial filters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialFilters = {
      payment_type: urlParams.get('payment_type') || null,
      start_date: urlParams.get('start_date') || null,
      end_date: urlParams.get('end_date') || null
    };

    this.state = {
      payments: [],
      isLoading: false,
      currentPage: 1,
      totalRecords: 0,
      showAddPaymentModal: false,
      isSubmitting: false,
      filters: initialFilters
    };
    this.cancelTokenSource = null;
  }

  componentDidMount() {
    // Fetch with initial filters from URL
    this.fetchPayments(1);
  }

  componentWillUnmount() {
    // Cancel any pending API requests to prevent memory leaks
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Component unmounted');
    }
  }

  /**
   * @function fetchPayments
   * @param pageNo
   * @description Fetch paginated payment data for the clinic
   */
  fetchPayments = pageNo => {
    const { clinicId } = this.props;
    const { filters } = this.state;
    this.setState({ isLoading: true });

    getTelemedicinePayments(clinicId, pageNo, 10, filters)
      .then(response => {
        const getSuccess = getProp(["data", "success"]);
        const getData = getProp(["data", "data"]);
        const getErrors = getProp(["data", "errors"]);

        if (getSuccess(response)) {
          const data = getData(response);
          this.setState({
            payments: data.payments || [],
            totalRecords: data.total_records || 0,
            currentPage: pageNo,
            isLoading: false
          });
        } else if (getErrors(response)) {
          const { errors } = response.data;
          let error = errors;
          if (Array.isArray(errors)) {
            error = errors[0];
          }
          showErrorNotification(error.title || "Failed to fetch payments");
          this.setState({ isLoading: false });
        }
      })
      .catch(() => {
        showErrorNotification("Failed to fetch payments");
        this.setState({ isLoading: false });
      });
  };

  /**
   * @function toggleAddPaymentModal
   * @description Toggle the visibility of add payment modal
   */
  toggleAddPaymentModal = () => {
    this.setState(prevState => ({
      showAddPaymentModal: !prevState.showAddPaymentModal
    }));
  };

  /**
   * @function handleAddPayment
   * @param data
   * @description Submit manual payment entry
   */
  handleAddPayment = data => {
    this.setState({ isSubmitting: true });

    createManualPayment(data)
      .then(response => {
        const getSuccess = getProp(["data", "success"]);
        const getMessage = getProp(["data", "message"]);
        const getErrors = getProp(["data", "errors"]);

        if (getSuccess(response)) {
          const message = getMessage(response) || "Payment added successfully";
          showSuccessNotification(message);
          this.toggleAddPaymentModal();
          // Refresh the payments list
          this.fetchPayments(this.state.currentPage);
        } else if (getErrors(response)) {
          const { errors } = response.data;
          let error = errors;
          if (Array.isArray(errors)) {
            error = errors[0];
          }
          showErrorNotification(error.title || error.message || "Failed to add payment");
        }
        this.setState({ isSubmitting: false });
      })
      .catch(error => {
        const errorMessage = getProp(["response", "data", "error", "message"])(error) || 
                           getProp(["response", "data", "message"])(error) || 
                           "Failed to add payment";
        showErrorNotification(errorMessage);
        this.setState({ isSubmitting: false });
      });
  };

  /**
   * @function onPageChange
   * @param page
   * @description Handle pagination page change
   */
  onPageChange = page => {
    this.fetchPayments(page);
  };

  /**
   * @function handleFilterChange
   * @param filters
   * @description Handle filter changes and update URL
   */
  handleFilterChange = filters => {
    // Update URL with new filters
    const urlParams = new URLSearchParams(window.location.search);

    // Update or remove each filter in URL
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        urlParams.set(key, filters[key]);
      } else {
        urlParams.delete(key);
      }
    });

    // Update browser URL without page reload
    const newUrl = window.location.pathname +
      (urlParams.toString() ? '?' + urlParams.toString() : '');
    window.history.replaceState({}, '', newUrl);

    this.setState({ filters }, () => {
      // Reset to first page when filters change
      this.fetchPayments(1);
    });
  };

  render() {
    const {
      payments,
      isLoading,
      currentPage,
      totalRecords,
      showAddPaymentModal,
      isSubmitting,
      filters
    } = this.state;

    return (
      <Spin spinning={isLoading}>
        <PaymentsView
          payments={payments}
          currentPage={currentPage}
          totalRecords={totalRecords}
          onPageChange={this.onPageChange}
          toggleAddPaymentModal={this.toggleAddPaymentModal}
          filters={filters}
          onFilterChange={this.handleFilterChange}
        />
        {showAddPaymentModal && (
          <AddPaymentModal
            visible={showAddPaymentModal}
            onCancel={this.toggleAddPaymentModal}
            onSubmit={this.handleAddPayment}
            isSubmitting={isSubmitting}
          />
        )}
      </Spin>
    );
  }
}